
import { RouterProvider } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { CookieConsentProvider } from "./context/CookieConsentContext";
import { useMaintenanceMode } from "./context/MaintenanceContext";
import { useAuth } from "./hooks/useAuth";
import { useEffect, useMemo, useState, Suspense, lazy } from "react";
import { createAppRouter } from "./routes";
import { Toaster } from "./components/ui/sonner";
import { prefetchLeagues } from "./hooks/queries/useLeaguesQuery";
import { preloadUserRole } from "./hooks/useRole";
import { prefetchFixtures } from "./hooks/fixtures/useFixturesQuery";

// Create a QueryClient instance with optimized settings
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: (failureCount, error) => {
        // Only retry network errors, not server errors
        if (error instanceof Error && error.message.includes('network')) {
          return failureCount < 3;
        }
        return false;
      },
      staleTime: 10 * 60 * 1000, // 10 minutes stale time (increased from 5)
      gcTime: 30 * 60 * 1000, // 30 minutes garbage collection (increased from 15)
    },
  },
});

// Loading component for Suspense boundaries
const AppLoading = () => (
  <div className="flex items-center justify-center h-screen bg-league-background">
    <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-league-primary"></div>
  </div>
);

function AppRoutes() {
  const { isMaintenanceMode, isLoading: maintenanceLoading } = useMaintenanceMode();
  const { user, isLoading: authLoading } = useAuth();
  const [isPreloading, setIsPreloading] = useState(true);
  
  // Preload critical data for faster rendering
  useEffect(() => {
    const preloadData = async () => {
      if (user) {
        // Preload user role in parallel with other data
        const rolePromise = preloadUserRole(user.id);
        
        // Prefetch common data
        const leaguesPromise = prefetchLeagues(queryClient);
        
        // Prefetch fixtures for the upcoming matches on dashboard
        const fixturesPromise = prefetchFixtures(queryClient);
        
        // Wait for all preloading to complete
        await Promise.all([rolePromise, leaguesPromise, fixturesPromise]);
      }
      setIsPreloading(false);
    };
    
    if (!authLoading && user !== null) {
      preloadData();
    } else if (!authLoading) {
      setIsPreloading(false);
    }
  }, [user, authLoading]);
  
  // Use useMemo to prevent unnecessary router recreation
  const router = useMemo(() => {
    // Only create router when maintenance mode and auth status are determined
    const isLoading = maintenanceLoading || authLoading || isPreloading;
    
    if (isLoading) {
      return null;
    }
    
    return createAppRouter(isMaintenanceMode, user, false);
  }, [isMaintenanceMode, maintenanceLoading, user, authLoading, isPreloading]);

  // Show loading state while we determine maintenance mode status or auth status
  if (!router) {
    return <AppLoading />;
  }

  return (
    <Suspense fallback={<AppLoading />}>
      <RouterProvider router={router} />
    </Suspense>
  );
}

function App() {
  // Preload critical assets
  useEffect(() => {
    // Preload logo images
    const preloadImage = (src: string) => {
      const img = new Image();
      img.src = src;
    };
    
    preloadImage("/striveleague-uploads/a0055435-152e-4efa-8135-aae028cbd56d.png"); // Strive League Logo
    
    // Preconnect to Supabase
    const link = document.createElement('link');
    link.rel = 'preconnect';
    link.href = 'https://fcwnshllffolbezyaniy.supabase.co';
    document.head.appendChild(link);
    
    // Add performance observer for monitoring long tasks
    if (typeof PerformanceObserver !== 'undefined') {
      const observer = new PerformanceObserver((list) => {
        list.getEntries().forEach((entry) => {
          if (entry.duration > 50) { // Tasks blocking the main thread for more than 50ms
            console.warn(`Long task detected (${Math.round(entry.duration)}ms):`, entry);
          }
        });
      });
      
      observer.observe({ entryTypes: ['longtask'] });
    }

    // Register service worker for push notifications
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.register('/sw.js')
        .then(registration => {
          console.log('Service Worker registered successfully');
        })
        .catch(error => {
          console.log('Service Worker registration failed:', error);
        });
    }
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <CookieConsentProvider>
        <AppRoutes />
        <Toaster />
      </CookieConsentProvider>
    </QueryClientProvider>
  );
}

export default App;
